import { HdFixedStarSkyPosition } from './hd-fixed-star-sky.data';

/**
 * Zuivere reken- en tekengeometrie voor de fixed-star-sterrenkaart. Gnomonische
 * (tangent-plane) projectie gecentreerd op de doelster; noord boven, oost links.
 * Geen Angular-afhankelijkheden zodat dit los te unit-testen is.
 */

export interface SkyStar {
  readonly ra: number; // graden 0..360
  readonly dec: number; // graden
  readonly mag: number;
  readonly bv: number; // B–V kleurindex (NaN toegestaan)
}

/** Benoemde/aangeduide ster voor hover-tooltips en de namen-toggle. */
export interface SkyNamedStar {
  readonly l: string; // kort label (eigennaam of Griekse aanduiding)
  readonly t: string; // hover-titel
  readonly ra: number;
  readonly dec: number;
  readonly m: number; // magnitude
}

export interface SkyCatalog {
  readonly stars: readonly SkyStar[];
  /** IAU-afkorting -> lijst van polylijnen ([ [ra,dec], ... ]); ra in -180..180. */
  readonly lines: ReadonlyMap<string, readonly (readonly (readonly number[])[])[]>;
  readonly named: readonly SkyNamedStar[];
}

export interface SkyNamedLabel {
  readonly x: number;
  readonly y: number;
  readonly l: string;
  readonly t: string;
  /** Label links van de ster uitlijnen wanneer die dicht bij de rechterrand staat. */
  readonly anchorEnd: boolean;
}

export interface SkyDot {
  readonly cx: number;
  readonly cy: number;
  readonly r: number;
  readonly fill: string;
  /** Halo-straal (0 = geen gloed). */
  readonly glow: number;
}

export interface SkyChartGeometry {
  readonly graticule: readonly string[];
  readonly lines: readonly string[];
  readonly dots: readonly SkyDot[];
  /** Schermpositie van de doelster (null wanneer buiten beeld). */
  readonly target: { readonly cx: number; readonly cy: number } | null;
  /** Benoemde sterren in beeld (helderste eerst, begrensd) voor hover + namen-toggle. */
  readonly named: readonly SkyNamedLabel[];
}

export const SKY_W = 820;
export const SKY_H = 560;
const CX = SKY_W / 2;
const CY = SKY_H / 2;
const KR = 258; // projectiestraal in px voor het halve beeldveld

/**
 * Twee vaste zoomniveaus (geen continue zoom — houdt het licht voor de browser).
 * Niveau "uit" = het beeldveld uit de data (de sfeervolle, meest uitgezoomde stand).
 * Niveau "in" = dat beeldveld maal deze factor (een tikje ingezoomd, leesbaarder).
 * Bewust parametriseerbaar om mee te experimenteren.
 */
export const SKY_ZOOM_IN_FACTOR = 0.66;
const D2R = Math.PI / 180;

const norm360 = (d: number): number => ((d % 360) + 360) % 360;
const f1 = (n: number): string => n.toFixed(1);

/** B–V kleurindex naar een sterkleur (blauwwit → rood). */
export function bvColor(bv: number): string {
  if (isNaN(bv)) return '#eaf0ff';
  if (bv < 0.0) return '#cfe0ff';
  if (bv < 0.3) return '#eef3ff';
  if (bv < 0.6) return '#fff7e8';
  if (bv < 1.0) return '#ffe9c6';
  if (bv < 1.5) return '#ffd2a1';
  return '#ffb78a';
}

/** Sterstraal in px uit de magnitude (feller = groter), begrensd. */
export function magRadius(mag: number): number {
  return Math.max(0.5, Math.min(4.2, 0.65 + (6.6 - mag) * 0.5));
}

const GREEK: Readonly<Record<string, string>> = {
  ome: 'ω', om: 'ο', al: 'α', be: 'β', ga: 'γ', de: 'δ', ep: 'ε', ze: 'ζ', et: 'η', th: 'θ',
  io: 'ι', ka: 'κ', la: 'λ', mu: 'μ', nu: 'ν', xi: 'ξ', pi: 'π', rh: 'ρ', si: 'σ', ta: 'τ',
  up: 'υ', ph: 'φ', ch: 'χ', ps: 'ψ',
};
const SUPERSCRIPT: Readonly<Record<string, string>> = { '1': '¹', '2': '²', '3': '³' };
const BAYER_RE = /^(ome|om|al|be|ga|de|ep|ze|et|th|io|ka|la|mu|nu|xi|pi|rh|si|ta|up|ph|ch|ps)(?:-([0-9]))?([A-Za-z].*)$/;

/** Rauwe catalogus-Bayer ('alCar', 'ga-1And', '16Tau') → leesbare vorm ('α Car', 'γ¹ And', '16 Tau'). */
export function formatBayer(bayer: string): string {
  const greek = bayer.match(BAYER_RE);
  if (greek && GREEK[greek[1]]) {
    return `${GREEK[greek[1]]}${greek[2] ? SUPERSCRIPT[greek[2]] ?? greek[2] : ''} ${greek[3]}`;
  }
  const flamsteed = bayer.match(/^(\d+)([A-Za-z].*)$/);
  if (flamsteed) {
    return `${flamsteed[1]} ${flamsteed[2]}`;
  }
  return bayer;
}

interface Projected {
  readonly x: number;
  readonly y: number;
  readonly cosc: number;
}

function project(ra: number, dec: number, ra0: number, dec0: number, k: number): Projected {
  let da = norm360(ra - ra0) * D2R;
  if (da > Math.PI) da -= 2 * Math.PI;
  const d = dec * D2R;
  const d0 = dec0 * D2R;
  const cosc = Math.sin(d0) * Math.sin(d) + Math.cos(d0) * Math.cos(d) * Math.cos(da);
  const x = (Math.cos(d) * Math.sin(da)) / cosc;
  const y = (Math.cos(d0) * Math.sin(d) - Math.sin(d0) * Math.cos(d) * Math.cos(da)) / cosc;
  return { x: CX - k * x, y: CY - k * y, cosc };
}

export function kForFov(fov: number): number {
  return KR / Math.tan((fov / 2) * D2R);
}

function graticule(ra0: number, dec0: number, k: number, fov: number): string[] {
  const out: string[] = [];
  for (let dec = -80; dec <= 80; dec += 10) {
    if (Math.abs(dec - dec0) > fov * 0.85) continue;
    const pts: string[] = [];
    for (let ra = ra0 - fov; ra <= ra0 + fov; ra += 1.5) {
      const p = project(norm360(ra), dec, ra0, dec0, k);
      if (p.cosc > 0) pts.push(`${f1(p.x)} ${f1(p.y)}`);
    }
    if (pts.length > 1) out.push('M' + pts.join(' L'));
  }
  for (let ra = 0; ra < 360; ra += 15) {
    const dd = ((ra - ra0 + 540) % 360) - 180;
    if (Math.abs(dd) > fov * 0.85) continue;
    const pts: string[] = [];
    for (let dec = dec0 - fov; dec <= dec0 + fov; dec += 1.5) {
      if (dec < -89 || dec > 89) continue;
      const p = project(ra, dec, ra0, dec0, k);
      if (p.cosc > 0) pts.push(`${f1(p.x)} ${f1(p.y)}`);
    }
    if (pts.length > 1) out.push('M' + pts.join(' L'));
  }
  return out;
}

function constellationPaths(
  lines: SkyCatalog['lines'], con: string, ra0: number, dec0: number, k: number,
): string[] {
  const feat = lines.get(con);
  if (!feat) return [];
  const paths: string[] = [];
  for (const ls of feat) {
    let seg: string[] = [];
    for (const [ra, dec] of ls) {
      const p = project(norm360(ra < 0 ? ra + 360 : ra), dec, ra0, dec0, k);
      if (p.cosc > 0.2) {
        seg.push(`${f1(p.x)} ${f1(p.y)}`);
      } else {
        if (seg.length > 1) paths.push('M' + seg.join(' L'));
        seg = [];
      }
    }
    if (seg.length > 1) paths.push('M' + seg.join(' L'));
  }
  return paths;
}

function fieldDots(stars: readonly SkyStar[], ra0: number, dec0: number, k: number, fov: number): SkyDot[] {
  const lim = Math.cos(fov * 0.72 * D2R);
  const dots: SkyDot[] = [];
  for (const s of stars) {
    const p = project(s.ra, s.dec, ra0, dec0, k);
    if (p.cosc < lim) continue;
    if (p.x < -20 || p.x > SKY_W + 20 || p.y < -20 || p.y > SKY_H + 20) continue;
    const r = magRadius(s.mag);
    dots.push({ cx: +f1(p.x), cy: +f1(p.y), r: +r.toFixed(2), fill: bvColor(s.bv), glow: s.mag < 2.6 ? +(r * 3.4).toFixed(1) : 0 });
  }
  return dots;
}

const NAMED_MAX = 28;

/** Benoemde sterren in beeld (helderste eerst, begrensd); doelster zelf overgeslagen. */
function namedLabels(
  named: readonly SkyNamedStar[], ra0: number, dec0: number, k: number, fov: number, max: number,
): SkyNamedLabel[] {
  const lim = Math.cos(fov * 0.62 * D2R);
  const inView: (SkyNamedLabel & { m: number })[] = [];
  for (const s of named) {
    const p = project(s.ra, s.dec, ra0, dec0, k);
    if (p.cosc < lim) continue;
    if (Math.hypot(p.x - CX, p.y - CY) < 11) continue; // doelster zelf heeft al zijn pill
    if (p.x < 34 || p.x > SKY_W - 34 || p.y < 22 || p.y > SKY_H - 14) continue;
    inView.push({ x: +p.x.toFixed(1), y: +p.y.toFixed(1), l: s.l, t: s.t, anchorEnd: p.x > SKY_W * 0.6, m: s.m });
  }
  inView.sort((a, b) => a.m - b.m);
  return inView.slice(0, max).map(({ x, y, l, t, anchorEnd }) => ({ x, y, l, t, anchorEnd }));
}

/** Bouwt alle teken-geometrie voor één kaart. `fov` mag afwijken van entry.fov (bv. compact iets ingezoomd). */
export function buildSkyGeometry(
  entry: HdFixedStarSkyPosition, catalog: SkyCatalog, fov: number,
): SkyChartGeometry {
  const k = kForFov(fov);
  const t = project(entry.ra, entry.dec, entry.ra, entry.dec, k); // = centrum
  return {
    graticule: graticule(entry.ra, entry.dec, k, fov),
    lines: constellationPaths(catalog.lines, entry.con, entry.ra, entry.dec, k),
    dots: fieldDots(catalog.stars, entry.ra, entry.dec, k, fov),
    target: { cx: +f1(t.x), cy: +f1(t.y) },
    named: namedLabels(catalog.named, entry.ra, entry.dec, k, fov, NAMED_MAX),
  };
}
